import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  DollarSign, Plus, Search, X, Loader2, CheckCircle,
  CreditCard, Users, History, AlertCircle, ChevronDown, ChevronUp,
  RefreshCw, Ban, BookOpen
} from 'lucide-react';
import { toast } from 'sonner';
import { usePublicSettings } from '../../contexts/PublicSettingsContext';
import { formatMoney } from '../../lib/currency';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const METHOD_LABELS = {
  manual:   { label: 'Manual',      color: 'bg-[#1A2E6E]/10 text-[#1A2E6E]' },
  pix:      { label: 'PIX',         color: 'bg-[#4CAF50]/10 text-[#4CAF50]' },
  transfer: { label: 'Transfer',    color: 'bg-[#F5A623]/10 text-[#F5A623]' },
  cash:     { label: 'Cash',        color: 'bg-[#9C27B0]/10 text-[#9C27B0]' },
  courtesy: { label: 'Courtesy',    color: 'bg-[#D72B2B]/10 text-[#D72B2B]' },
  stripe:   { label: 'Stripe',      color: 'bg-[#635BFF]/10 text-[#635BFF]' },
};

const STATUS_COLORS = {
  active:    'bg-[#4CAF50]/10 text-[#4CAF50]',
  expired:   'bg-[#F5A623]/10 text-[#F5A623]',
  cancelled: 'bg-[#D72B2B]/10 text-[#D72B2B]',
  pending:   'bg-[#1A2E6E]/10 text-[#1A2E6E]',
};

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

// ─── Register Payment Modal ──────────────────────────────────────────────────
const PaymentModal = ({ students, plans, onClose, onSaved }) => {
  const { settings } = usePublicSettings();
  const [form, setForm] = useState({
    student_id: '',
    plan_id: '',
    amount: '',
    currency: settings.default_currency || 'EUR',
    method: 'transfer',
    notes: '',
    paid_at: new Date().toISOString().slice(0, 10),
    coupon_code: '',
    create_enrollment: true,
  });
  const [saving, setSaving] = useState(false);
  const [couponResult, setCouponResult] = useState(null); // { valid, savings, discounted_amount, message }
  const [validatingCoupon, setValidatingCoupon] = useState(false);

  // Auto-fill amount when plan is selected
  const handlePlanChange = (planId) => {
    const plan = plans.find(p => p.id === planId);
    setForm(f => ({
      ...f,
      plan_id: planId,
      amount: plan ? plan.price_brl.toString() : f.amount,
      currency: plan?.currency || f.currency,
    }));
    setCouponResult(null);
  };

  const validateCoupon = async () => {
    if (!form.coupon_code.trim()) return;
    if (!form.amount || isNaN(parseFloat(form.amount))) {
      toast.error('Enter an amount before applying a coupon');
      return;
    }
    setValidatingCoupon(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/coupons/validate`, {
        code: form.coupon_code.trim(),
        plan_id: form.plan_id || null,
        original_amount: parseFloat(form.amount),
      });
      setCouponResult(res.data);
      if (!res.data.valid) toast.error(res.data.message);
    } catch {
      toast.error('Failed to validate coupon');
    } finally {
      setValidatingCoupon(false);
    }
  };

  const finalAmount = couponResult?.valid
    ? couponResult.discounted_amount
    : (parseFloat(form.amount) || 0);

  const handleSave = async () => {
    if (!form.student_id) { toast.error('Select a student'); return; }
    if (!form.amount || isNaN(parseFloat(form.amount))) { toast.error('Enter a valid amount'); return; }
    if (form.create_enrollment && !form.plan_id) { toast.error('Select a plan to create enrollment'); return; }

    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/admin/payments`, {
        ...form,
        amount: parseFloat(form.amount),
        plan_id: form.plan_id || null,
        paid_at: form.paid_at ? new Date(form.paid_at).toISOString() : null,
        coupon_code: form.coupon_code.trim() || null,
      });
      toast.success('Payment registered' + (form.create_enrollment ? ' and enrollment created' : ''));
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to register payment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] flex flex-col"
      >
        <div className="flex items-center justify-between p-6 border-b border-[#E8EAF0]">
          <h2 className="text-lg font-bold text-[#1A2E6E]">Register Payment</h2>
          <button onClick={onClose} className="p-2 hover:bg-[#1A2E6E]/5 rounded-lg">
            <X size={20} className="text-[#1A2E6E]/60" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto space-y-4">
          {/* Student */}
          <div>
            <label className="block text-sm font-medium text-[#1A2E6E] mb-1">Student *</label>
            <select
              value={form.student_id}
              onChange={e => setForm(f => ({ ...f, student_id: e.target.value }))}
              className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none bg-white"
            >
              <option value="">— select student —</option>
              {students.map(s => (
                <option key={s.id} value={s.id}>{s.name} ({s.email})</option>
              ))}
            </select>
          </div>

          {/* Plan */}
          <div>
            <label className="block text-sm font-medium text-[#1A2E6E] mb-1">
              Plan <span className="text-[#1A2E6E]/40 font-normal">(required if creating enrollment)</span>
            </label>
            <select
              value={form.plan_id}
              onChange={e => handlePlanChange(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none bg-white"
            >
              <option value="">— no plan / ad-hoc —</option>
              {plans.map(p => (
                <option key={p.id} value={p.id}>
                  {p.name} — {p.credits} lessons — {formatMoney(p.price_brl, p.currency || 'EUR')}
                </option>
              ))}
            </select>
          </div>

          {/* Amount + currency */}
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-[#1A2E6E] mb-1">Amount *</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.amount}
                onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                placeholder="320.00"
                className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1A2E6E] mb-1">Currency</label>
              <select
                value={form.currency}
                onChange={e => setForm(f => ({ ...f, currency: e.target.value }))}
                className="w-full px-3 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none bg-white"
              >
                <option value="EUR">EUR (€)</option>
                <option value="BRL">BRL (R$)</option>
                <option value="USD">USD ($)</option>
                <option value="GBP">GBP (£)</option>
              </select>
            </div>
          </div>

          {/* Method + date */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-[#1A2E6E] mb-1">Method *</label>
              <select
                value={form.method}
                onChange={e => setForm(f => ({ ...f, method: e.target.value }))}
                className="w-full px-3 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none bg-white"
              >
                <option value="transfer">Transfer</option>
                <option value="pix">PIX</option>
                <option value="cash">Cash</option>
                <option value="manual">Manual</option>
                <option value="courtesy">Courtesy</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1A2E6E] mb-1">Payment Date</label>
              <input
                type="date"
                value={form.paid_at}
                onChange={e => setForm(f => ({ ...f, paid_at: e.target.value }))}
                className="w-full px-3 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none"
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-[#1A2E6E] mb-1">Notes <span className="text-[#1A2E6E]/40 font-normal">(internal)</span></label>
            <textarea
              rows={2}
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="e.g. PIX received on WhatsApp, screenshot on file"
              className="w-full px-4 py-2.5 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none resize-none text-sm"
            />
          </div>

          {/* Coupon code */}
          <div>
            <label className="block text-sm font-medium text-[#1A2E6E] mb-1">
              Coupon Code <span className="text-[#1A2E6E]/40 font-normal">(optional)</span>
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={form.coupon_code}
                onChange={e => { setForm(f => ({ ...f, coupon_code: e.target.value.toUpperCase() })); setCouponResult(null); }}
                onKeyDown={e => e.key === 'Enter' && validateCoupon()}
                placeholder="e.g. WELCOME20"
                className="flex-1 px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none font-mono uppercase tracking-widest text-sm"
              />
              <button
                type="button"
                onClick={validateCoupon}
                disabled={validatingCoupon || !form.coupon_code.trim()}
                className="px-4 py-3 rounded-xl bg-[#1A2E6E] text-white text-sm font-medium hover:bg-[#16275C] disabled:opacity-40 transition-colors flex items-center gap-1.5"
              >
                {validatingCoupon ? <Loader2 size={14} className="animate-spin" /> : 'Apply'}
              </button>
            </div>
            {couponResult && (
              <div className={`mt-2 px-3 py-2 rounded-lg text-sm flex items-center gap-2 ${
                couponResult.valid
                  ? 'bg-[#4CAF50]/8 text-[#4CAF50] border border-[#4CAF50]/20'
                  : 'bg-[#D72B2B]/8 text-[#D72B2B] border border-[#D72B2B]/20'
              }`}>
                {couponResult.valid
                  ? <><CheckCircle size={14} /> {couponResult.message} → Final: {formatMoney(couponResult.discounted_amount, form.currency)}</>
                  : <><AlertCircle size={14} /> {couponResult.message}</>
                }
              </div>
            )}
          </div>

          {/* Create enrollment toggle */}
          <label className="flex items-center gap-3 cursor-pointer p-3 bg-[#F8F9FC] rounded-xl border border-[#E8EAF0]">
            <input
              type="checkbox"
              checked={form.create_enrollment}
              onChange={e => setForm(f => ({ ...f, create_enrollment: e.target.checked }))}
              className="w-4 h-4 accent-[#1A2E6E]"
            />
            <div>
              <p className="text-sm font-medium text-[#1A2E6E]">Create enrollment automatically</p>
              <p className="text-xs text-[#1A2E6E]/50">Activates credits and approves student if pending</p>
            </div>
          </label>
        </div>

        <div className="flex gap-3 p-6 border-t border-[#E8EAF0]">
          <button
            onClick={onClose}
            className="flex-1 py-3 rounded-xl border border-[#E8EAF0] text-[#1A2E6E] hover:bg-[#F5F7FA] font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 py-3 rounded-xl bg-[#4CAF50] text-white hover:bg-[#388E3C] font-medium disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
            Register{finalAmount > 0 ? ` · ${formatMoney(finalAmount, form.currency)}` : ''}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

// ─── Credit Adjust Modal ─────────────────────────────────────────────────────
const CreditAdjustModal = ({ enrollment, onClose, onSaved }) => {
  const [delta, setDelta] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    const d = parseInt(delta);
    if (isNaN(d) || d === 0) { toast.error('Enter a non-zero number of credits'); return; }
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/enrollments/${enrollment.id}/credits`, {
        credits_delta: d,
        notes: notes || undefined,
      });
      toast.success('Credits adjusted');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to adjust credits');
    } finally {
      setSaving(false);
    }
  };

  const remaining = enrollment.credits_total - enrollment.credits_used;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-2xl shadow-xl w-full max-w-sm"
      >
        <div className="flex items-center justify-between p-6 border-b border-[#E8EAF0]">
          <div>
            <h2 className="text-lg font-bold text-[#1A2E6E]">Adjust Credits</h2>
            <p className="text-sm text-[#1A2E6E]/60">{enrollment.plan_name} — {remaining} remaining</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[#1A2E6E]/5 rounded-lg">
            <X size={20} className="text-[#1A2E6E]/60" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#1A2E6E] mb-1">
              Credits to add/remove <span className="text-[#1A2E6E]/40">(use negative to remove)</span>
            </label>
            <input
              type="number"
              value={delta}
              onChange={e => setDelta(e.target.value)}
              placeholder="e.g. +2 or -1"
              className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#1A2E6E] mb-1">Reason <span className="text-[#1A2E6E]/40 font-normal">(optional)</span></label>
            <input
              type="text"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="e.g. Courtesy class, rescheduled"
              className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none"
            />
          </div>
        </div>
        <div className="flex gap-3 p-6 border-t border-[#E8EAF0]">
          <button onClick={onClose} className="flex-1 py-3 rounded-xl border border-[#E8EAF0] text-[#1A2E6E] hover:bg-[#F5F7FA] font-medium">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 py-3 rounded-xl bg-[#1A2E6E] text-white hover:bg-[#16275C] font-medium disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : null}
            Apply
          </button>
        </div>
      </motion.div>
    </div>
  );
};

// ─── Student Row (collapsible) ────────────────────────────────────────────────
const StudentBillingRow = ({ student, onRefresh }) => {
  const [open, setOpen] = useState(false);
  const [enrollments, setEnrollments] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [adjustingEnrollment, setAdjustingEnrollment] = useState(null);

  const loadDetail = useCallback(async () => {
    if (loadingDetail) return;
    setLoadingDetail(true);
    try {
      const [enRes, pyRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/enrollments?student_id=${student.id}&page_size=200`),
        axios.get(`${API_URL}/api/admin/payments?student_id=${student.id}&page_size=200`),
      ]);
      setEnrollments(enRes.data.data ?? enRes.data);
      setPayments(pyRes.data.data ?? pyRes.data);
    } catch {
      toast.error('Failed to load billing details');
    } finally {
      setLoadingDetail(false);
    }
  }, [student.id, loadingDetail]);

  const handleToggle = () => {
    if (!open) loadDetail();
    setOpen(o => !o);
  };

  const cancelEnrollment = async (eid) => {
    if (!window.confirm('Cancel this enrollment?')) return;
    try {
      await axios.put(`${API_URL}/api/admin/enrollments/${eid}/cancel`);
      toast.success('Enrollment cancelled');
      loadDetail();
    } catch {
      toast.error('Failed to cancel');
    }
  };

  const activeEnrollment = enrollments.find(e => e.status === 'active');

  return (
    <div className="border-b border-[#E8EAF0] last:border-b-0">
      <button
        onClick={handleToggle}
        className="w-full flex items-center gap-4 p-4 lg:p-5 hover:bg-[#F8F9FC] transition-colors text-left"
      >
        <div className="w-10 h-10 rounded-full bg-[#1A2E6E]/10 flex items-center justify-center text-[#1A2E6E] font-bold shrink-0">
          {student.name?.charAt(0)?.toUpperCase() || '?'}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-[#1A2E6E] truncate">{student.name}</p>
          <p className="text-xs text-[#1A2E6E]/50 truncate">{student.email}</p>
        </div>
        {student.active_enrollment ? (
          <div className="hidden sm:flex items-center gap-2 text-sm">
            <span className="px-2 py-0.5 rounded-full bg-[#4CAF50]/10 text-[#4CAF50] text-xs font-medium">
              {student.active_enrollment.credits_remaining} lessons left
            </span>
            <span className="text-[#1A2E6E]/40 text-xs">{student.active_enrollment.plan_name}</span>
          </div>
        ) : (
          <span className="hidden sm:block text-xs text-[#1A2E6E]/30">No active plan</span>
        )}
        {open ? <ChevronUp size={16} className="text-[#1A2E6E]/40 shrink-0" /> : <ChevronDown size={16} className="text-[#1A2E6E]/40 shrink-0" />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 lg:px-5 space-y-4">
              {loadingDetail ? (
                <div className="flex justify-center py-6">
                  <Loader2 className="w-6 h-6 text-[#D72B2B] animate-spin" />
                </div>
              ) : (
                <>
                  {/* Enrollments */}
                  <div>
                    <p className="text-xs font-semibold text-[#1A2E6E]/50 uppercase tracking-wide mb-2">Enrollments</p>
                    {enrollments.length === 0 ? (
                      <p className="text-sm text-[#1A2E6E]/40 italic">No enrollments yet</p>
                    ) : (
                      <div className="space-y-2">
                        {enrollments.map(e => (
                          <div key={e.id} className="flex items-center gap-3 p-3 bg-[#F8F9FC] rounded-xl text-sm">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium text-[#1A2E6E]">{e.plan_name}</span>
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[e.status] || 'bg-gray-100 text-gray-600'}`}>
                                  {e.status}
                                </span>
                              </div>
                              <p className="text-[#1A2E6E]/50 text-xs mt-0.5">
                                {e.credits_used}/{e.credits_total} used
                                {' · '}expires {fmtDate(e.expires_at)}
                              </p>
                            </div>
                            {e.status === 'active' && (
                              <div className="flex gap-1 shrink-0">
                                <button
                                  onClick={() => setAdjustingEnrollment(e)}
                                  title="Adjust credits"
                                  className="p-1.5 text-[#F5A623] hover:bg-[#F5A623]/10 rounded-lg transition-colors"
                                >
                                  <RefreshCw size={14} />
                                </button>
                                <button
                                  onClick={() => cancelEnrollment(e.id)}
                                  title="Cancel enrollment"
                                  className="p-1.5 text-[#D72B2B] hover:bg-[#D72B2B]/10 rounded-lg transition-colors"
                                >
                                  <Ban size={14} />
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Payments */}
                  <div>
                    <p className="text-xs font-semibold text-[#1A2E6E]/50 uppercase tracking-wide mb-2">Payments</p>
                    {payments.length === 0 ? (
                      <p className="text-sm text-[#1A2E6E]/40 italic">No payments recorded</p>
                    ) : (
                      <div className="space-y-1.5">
                        {payments.map(p => {
                          const m = METHOD_LABELS[p.method] || METHOD_LABELS.manual;
                          return (
                            <div key={p.id} className="flex items-center gap-3 px-3 py-2 rounded-lg bg-[#F8F9FC] text-sm">
                              <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium shrink-0 ${m.color}`}>{m.label}</span>
                              <span className="font-semibold text-[#1A2E6E]">{formatMoney(p.amount, p.currency)}</span>
                              <span className="text-[#1A2E6E]/40 text-xs ml-auto">{fmtDate(p.paid_at || p.created_at)}</span>
                              {p.notes && (
                                <span className="hidden md:block text-[#1A2E6E]/40 text-xs truncate max-w-[120px]" title={p.notes}>
                                  {p.notes}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {adjustingEnrollment && (
          <CreditAdjustModal
            enrollment={adjustingEnrollment}
            onClose={() => setAdjustingEnrollment(null)}
            onSaved={() => { setAdjustingEnrollment(null); loadDetail(); onRefresh(); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
export const AdminBilling = () => {
  const [students, setStudents] = useState([]);
  const [plans, setPlans]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [paymentModal, setPaymentModal] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [stuRes, planRes, enRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/students?page_size=200`),
        axios.get(`${API_URL}/api/admin/plans?page_size=200`),
        axios.get(`${API_URL}/api/admin/enrollments?status=active&page_size=200`),
      ]);

      // Build quick lookup of active enrollment per student
      const activeMap = {};
      for (const e of (enRes.data.data ?? enRes.data)) {
        if (!activeMap[e.student_id]) activeMap[e.student_id] = e;
      }

      const studentsWithBilling = (stuRes.data.data ?? stuRes.data).map(s => ({
        ...s,
        active_enrollment: activeMap[s.id] || null,
      }));

      setStudents(studentsWithBilling);
      setPlans((planRes.data.data ?? planRes.data).filter(p => p.is_active));
    } catch {
      toast.error('Failed to load billing data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = students.filter(s =>
    s.name?.toLowerCase().includes(search.toLowerCase()) ||
    s.email?.toLowerCase().includes(search.toLowerCase())
  );

  const totalActive   = students.filter(s => s.active_enrollment).length;
  const totalStudents = students.length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-[#D72B2B] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-[#1A2E6E] font-['Sora']">Billing</h1>
          <p className="text-[#1A2E6E]/60 mt-1">Manage payments, enrollments and credits</p>
        </div>
        <button
          onClick={() => setPaymentModal(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-[#4CAF50] text-white rounded-xl hover:bg-[#388E3C] transition-colors text-sm font-medium shrink-0"
        >
          <Plus size={16} /> Register Payment
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-[#E8EAF0] p-5">
          <Users className="w-6 h-6 text-[#1A2E6E] mb-2" />
          <p className="text-2xl font-bold text-[#1A2E6E]">{totalStudents}</p>
          <p className="text-sm text-[#1A2E6E]/60">Total Students</p>
        </div>
        <div className="bg-white rounded-xl border border-[#E8EAF0] p-5">
          <BookOpen className="w-6 h-6 text-[#4CAF50] mb-2" />
          <p className="text-2xl font-bold text-[#1A2E6E]">{totalActive}</p>
          <p className="text-sm text-[#1A2E6E]/60">With Active Plan</p>
        </div>
        <div className="bg-white rounded-xl border border-[#E8EAF0] p-5 col-span-2 lg:col-span-1">
          <DollarSign className="w-6 h-6 text-[#F5A623] mb-2" />
          <p className="text-2xl font-bold text-[#1A2E6E]">{plans.length}</p>
          <p className="text-sm text-[#1A2E6E]/60">Active Plans</p>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#1A2E6E]/40" />
        <input
          type="text"
          placeholder="Search students..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none"
        />
      </div>

      {/* Students list */}
      <div className="bg-white rounded-xl border border-[#E8EAF0] overflow-hidden">
        {filtered.length === 0 ? (
          <div className="p-12 text-center">
            <AlertCircle className="w-12 h-12 text-[#1A2E6E]/20 mx-auto mb-4" />
            <p className="text-[#1A2E6E]/60">
              {search ? 'No students match your search' : 'No students found'}
            </p>
          </div>
        ) : (
          <div>
            {filtered.map(student => (
              <StudentBillingRow
                key={student.id}
                student={student}
                onRefresh={fetchData}
              />
            ))}
          </div>
        )}
      </div>

      {/* Payment modal */}
      <AnimatePresence>
        {paymentModal && (
          <PaymentModal
            students={students}
            plans={plans}
            onClose={() => setPaymentModal(false)}
            onSaved={() => { setPaymentModal(false); fetchData(); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminBilling;
